/**
 * Formatter batch-run safety helpers — "never again" package (2026-08-14).
 *
 * The desktop Formatter (local Word engine batch page) destroyed real user
 * files: a failed exists-check silently proceeded to overwrite, and the
 * default write mode was 'overwrite'. This module holds the PURE logic
 * (no fs/IPC/React) shared by the main-process IPC handlers
 * (src/main/main.ts) and the renderer's Formatter page
 * (src/renderer/components/EditorComponents.tsx), so the safety rules are
 * defined once and are directly unit-testable without Electron.
 *
 * Law:
 *   1. An error checking for existing output files BLOCKS the run — it must
 *      never be swallowed and treated as "no conflicts."
 *   2. The default write mode is 'rename' — 'overwrite' only happens on an
 *      explicit user choice.
 *   3. A batch run shows the confirmation (file count, forma, output folder,
 *      exact files that would be affected) whenever something could actually
 *      be clobbered — i.e. a real conflict exists on disk, or the conflict
 *      check itself failed. AMENDED 2026-08-21 (Josh): a run with ZERO real
 *      conflicts no longer stops for a confirmation it can only answer one
 *      way; it proceeds in the safe 'rename' mode, which is a no-op when
 *      nothing collides. "Real" excludes false positives — when originals
 *      are archived, an existing output file that IS one of the inputs about
 *      to be MOVED out of the folder is not a conflict.
 *   4. Any file about to be overwritten is backed up first (see
 *      src/engine/word-engine.ts's backupExistingFile — needs fs, lives in
 *      the engine, not here).
 */

export type OverwriteMode = 'overwrite' | 'rename';

/**
 * Resolve a caller-supplied write mode to a safe default. Never defaults to
 * 'overwrite' — only an explicit 'overwrite' string produces 'overwrite'.
 * Anything else (undefined, null, '', 'ask', a typo) resolves to 'rename'.
 */
export function resolveOverwriteMode(mode?: string | null): OverwriteMode {
  return mode === 'overwrite' ? 'overwrite' : 'rename';
}

export interface ConflictEntry {
  file: string;
  outputPath: string;
}

/** Data shown in the mandatory pre-batch confirmation modal. */
export interface FormatConfirmation {
  fileCount: number;
  /** Full paths of every input file — the modal lists them verbatim. */
  filePaths: string[];
  /** null for a PDF-only conversion run (no forma involved). */
  formaName: string | null;
  outputDir: string;
  /** Set ⇒ originals move into a dated folder here before the run. */
  archiveDir?: string;
  conflicts: ConflictEntry[];
  /** Set ⇒ the output folder could NOT be checked for existing files (law 1).
      The modal says so and withholds the Overwrite option — the run can only
      proceed in the non-destructive 'rename' mode. */
  checkFailed?: string;
}

export function buildFormatConfirmation(opts: {
  filePaths: string[];
  formaName: string | null;
  outputDir: string;
  conflicts: ConflictEntry[];
}): FormatConfirmation {
  return {
    fileCount: opts.filePaths.length,
    filePaths: opts.filePaths,
    formaName: opts.formaName,
    outputDir: opts.outputDir,
    conflicts: opts.conflicts,
  };
}
